import { useState, useEffect, useMemo, useCallback } from 'react'
import { subMonths } from 'date-fns'
import { PayPeriodData } from '../../../models/enhancement/pay-period-data'
import { enhancementReportService } from '../../../services/enhancementReportService'

function findPeriodContainingDate(
  sortedPeriods: PayPeriodData[],
  date: Date
): PayPeriodData | null {
  return sortedPeriods.find(period => {
    const start = new Date(period.periodStart)
    const end = new Date(period.periodEnd)
    return date >= start && date <= end
  }) ?? null
}

function calculateDefaultToPeriod(
  sortedPeriods: PayPeriodData[],
  today: Date
): PayPeriodData | null {
  const currentPeriod = findPeriodContainingDate(sortedPeriods, today)
  
  if (currentPeriod) {
    return currentPeriod
  }
  
  return sortedPeriods[sortedPeriods.length - 1] || null
}

function findTwelveMonthAgoPeriod(
  sortedPeriods: PayPeriodData[],
  today: Date
): PayPeriodData | null {
  const twelveMonthsAgo = subMonths(today, 12)
  
  const exactPeriod = findPeriodContainingDate(sortedPeriods, twelveMonthsAgo)
  if (exactPeriod) {
    return exactPeriod
  }
  return sortedPeriods.reduce<PayPeriodData | null>((nearest, period) => {
    if (!nearest) return period
    
    const periodEnd = new Date(period.periodEnd)
    const nearestEnd = new Date(nearest.periodEnd)
    
    const periodDistance = Math.abs(periodEnd.getTime() - twelveMonthsAgo.getTime())
    const nearestDistance = Math.abs(nearestEnd.getTime() - twelveMonthsAgo.getTime())
    
    return periodDistance < nearestDistance ? period : nearest
  }, null)
}

function calculateDefaultFromPeriod(
  sortedPeriods: PayPeriodData[],
  today: Date,
  earliestPayPeriod: PayPeriodData | null
): PayPeriodData | null {
  const twelveMonthAgoPeriod = findTwelveMonthAgoPeriod(sortedPeriods, today)
  
  if (earliestPayPeriod && twelveMonthAgoPeriod) {
    const earliestPeriodEnd = new Date(earliestPayPeriod.periodEnd)
    const twelveMonthPeriodEnd = new Date(twelveMonthAgoPeriod.periodEnd)
    
    return twelveMonthPeriodEnd < earliestPeriodEnd
      ? earliestPayPeriod
      : twelveMonthAgoPeriod
  }
  
  if (earliestPayPeriod) {
    return earliestPayPeriod
  }
  
  return twelveMonthAgoPeriod
}

export interface UsePayPeriodsReturn {
  payPeriods: PayPeriodData[];
  payPeriodsByYear: Record<number, PayPeriodData[]>;
  availableYears: number[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  defaultFromPeriod: PayPeriodData | null;
  defaultToPeriod: PayPeriodData | null;
}

export function usePayPeriods(): UsePayPeriodsReturn {
  const [payPeriods, setPayPeriods] = useState<PayPeriodData[]>([])
  const [payPeriodsByYear, setPayPeriodsByYear] = useState<Record<number, PayPeriodData[]>>({})
  const [availableYears, setAvailableYears] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [earliestPayPeriod, setEarliestPayPeriod] = useState<PayPeriodData | null>(null)

  const { defaultFromPeriod, defaultToPeriod } = useMemo(() => {
    if (payPeriods.length === 0) {
      return { defaultFromPeriod: null, defaultToPeriod: null }
    }
    
    const sortedPeriods = [...payPeriods].sort((a, b) =>
      new Date(a.periodEnd).getTime() - new Date(b.periodEnd).getTime()
    )

    const today = new Date()
    const toPeriod = calculateDefaultToPeriod(sortedPeriods, today)
    const fromPeriod = calculateDefaultFromPeriod(sortedPeriods, today, earliestPayPeriod)

    return { defaultFromPeriod: fromPeriod, defaultToPeriod: toPeriod }
  }, [payPeriods, earliestPayPeriod])
  
  const fetchPayPeriods = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const [payPeriodsResponse, earliestPayPeriodResponse] = await Promise.all([
        enhancementReportService.getPayPeriods(),
        enhancementReportService.getEarliestPayPeriod().catch(err => {
          console.warn('Failed to fetch earliest pay period, using fallback:', err)
          return null
        })
      ])
      
      if (payPeriodsResponse.payPeriodsByYear && payPeriodsResponse.availableYears) {
        setPayPeriodsByYear(payPeriodsResponse.payPeriodsByYear)
        setAvailableYears(payPeriodsResponse.availableYears)

        const allPayPeriods = Object.values(payPeriodsResponse.payPeriodsByYear).flat()
        setPayPeriods(allPayPeriods)
      } else {
        setPayPeriods([])
        setPayPeriodsByYear({})
        setAvailableYears([])
      }
      
      setEarliestPayPeriod(earliestPayPeriodResponse)
    } catch (err) {
      console.error('Error fetching pay periods:', err)
      setError('Failed to load pay periods. Please try again.')
      setPayPeriods([])
      setPayPeriodsByYear({})
      setAvailableYears([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPayPeriods()
  }, [fetchPayPeriods])

  return {
    payPeriods,
    payPeriodsByYear,
    availableYears,
    isLoading,
    error,
    refetch: fetchPayPeriods,
    defaultFromPeriod,
    defaultToPeriod
  }
}